import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { formatNum, apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { toast } from "sonner";
import { Building2, Wallet, Cpu, Receipt, Settings2, Percent } from "lucide-react";
import PlatformTenantDialog from "./PlatformTenantDialog";

const PLAN_VARIANT = { control: "success", growth: "warning", lite: "outline" };

export default function PlatformOverview() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);

  const load = useCallback(() => {
    api.get("/billing/platform/tenants").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = data?.tenants || [];
  const totalTokens = rows.reduce((s, r) => s + (r.ai_tokens || 0), 0);
  const totalOutstanding = rows.reduce((s, r) => s + (r.outstanding_sgd || 0), 0);
  const active = rows.filter((r) => r.subscription?.status === "active").length;

  return (
    <div className="space-y-5" data-testid="platform-overview">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={Wallet} label="Monthly recurring revenue" value={`S$${formatNum(data?.mrr_sgd || 0, { decimals: 0 })}`} accent />
        <Stat icon={Building2} label="Active paid tenants" value={active} />
        <Stat icon={Cpu} label="AI tokens (MTD)" value={formatNum(totalTokens)} />
        <Stat icon={Receipt} label="Outstanding" value={`S$${formatNum(totalOutstanding, { decimals: 0 })}`} />
        <Stat icon={Percent} label="Active discounts" value={`S$${formatNum(data?.active_discounts_sgd || 0, { decimals: 0 })}`} testid="stat-active-discounts" />
      </div>

      <div className="card-flat overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tenant</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead>
            <TableHead className="text-right">Seats</TableHead><TableHead className="text-right">MRR</TableHead>
            <TableHead className="text-right">Discounts</TableHead>
            <TableHead className="text-right">AI tokens</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.tenant.id} data-testid="platform-tenant-row">
                <TableCell><div className="font-medium text-sm">{r.tenant.name}</div><div className="text-[11px] text-muted-foreground">{r.tenant.regulatory_tier || "—"}</div></TableCell>
                <TableCell>{r.subscription?.package_code ? <Badge variant={PLAN_VARIANT[r.subscription.package_code] || "outline"}>{r.subscription.package_code}</Badge> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                <TableCell><Badge variant={r.subscription?.status === "active" ? "success" : r.subscription?.status === "trialing" ? "warning" : "outline"} className="text-[10px]">{r.subscription?.status || "none"}</Badge></TableCell>
                <TableCell className="text-right font-mono text-sm">{r.subscription?.seats || "—"}</TableCell>
                <TableCell className="text-right font-mono text-sm">{r.subscription?.monthly_price_sgd ? `S$${formatNum(r.subscription.monthly_price_sgd, { decimals: 0 })}` : "—"}</TableCell>
                <TableCell className="text-right font-mono text-sm" data-testid={`discount-cell-${r.tenant.id}`}>{r.active_discount_sgd ? <span className="text-[#00E65B]">−S${formatNum(r.active_discount_sgd, { decimals: 0 })}</span> : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-right font-mono text-sm">{formatNum(r.ai_tokens || 0)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{r.outstanding_sgd ? `S$${formatNum(r.outstanding_sgd, { decimals: 2 })}` : "—"}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => setSel(r.tenant)} data-testid={`manage-${r.tenant.id}`}><Settings2 className="h-3.5 w-3.5" /> Manage</Button></TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8 text-sm">No onboarded tenants.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {sel && <PlatformTenantDialog tenant={sel} onClose={() => setSel(null)} onChanged={load} />}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon className="h-4 w-4" /><span className="label-eyebrow text-[10px]">{label}</span></div>
      <div className={`font-mono text-2xl font-bold ${accent ? "text-accent" : ""}`}>{value}</div>
    </div>
  );
}
